import React from 'react';
import { useHistory } from 'react-router-dom';
import { Typography, Box, makeStyles } from '@material-ui/core';
import { DetailsLayout } from '../utilities/layouts/DetailsLayout';
import { ContentLayout } from '../utilities/layouts/ContentLayout';
import AccordionWrapper from '../utilities/Accordion/AccordionWrapper';
import Accordion from '../utilities/Accordion/Accordion';
import { PATH_CALENDAR_TUTORIAL, PATH_ONBOARDING_TUTORIAL } from '../../containers/RoutePaths';
import { COLOR_GREY } from '../utilities/MaterialWatchman';

const useStyles = makeStyles(theme => ({
    helpCentrePageContent: {
        '& h3.MuiTypography-h3': {
            color: theme.palette.primary.main,
            paddingBottom: theme.spacing(2),
            borderBottom: `1px solid ${COLOR_GREY.light}`,
        },
    },
}));

const emailWatchman = (
    <a style={{ color: 'inherit', textDecoration: 'inherit', fontWeight: 500 }} href="mailto:watchman@uhn.ca">
        watchman@uhn.ca
    </a>
);

const calendarTutorial = (
    <a style={{ color: 'inherit', textDecoration: 'inherit', fontWeight: 500 }} href={PATH_CALENDAR_TUTORIAL}>
        Calendar Tutorial
    </a>
);

const onboardingTutorial = (
    <a style={{ color: 'inherit', textDecoration: 'inherit', fontWeight: 500 }} href={PATH_ONBOARDING_TUTORIAL}>
        Onboarding Tutorial
    </a>
);

const accordionContentSec1 = [
    {
        title: 'How do I join Watchman?',
        description:
            'If you are eligible for Watchman, your doctor will introduce you to Watchman during one of your follow-up calls. Watchman is only available by invitation from doctors at participating clinics.',
    },
    {
        title: "I don't understand how to use the app. What can I do?",
        description: (
            <>
                The {onboardingTutorial} and {calendarTutorial} will provide you with an overview of the Watchman application and are located in the Settings
                tab of this application. For additional support, please contact {emailWatchman} and our Watchman Coordinator will be happy to set up a call to
                help answer your questions.
            </>
        ),
    },
    {
        title: 'What if I no longer want to use Watchman?',
        description: <>If you no longer want to use Watchman, please contact Watchman support at {emailWatchman}</>,
    },
    {
        title: 'Where can I find the Watchman Tutorial?',
        description: (
            <>
                The {onboardingTutorial} and {calendarTutorial} can be found here.
            </>
        ),
    },
];

const accordionContentSec2 = [
    {
        title: 'When do I complete my tasks?',
        description:
            "You will get an email from the Watchman team when it's time to complete your tasks. You will also be able to view the due date of your tasks on the Watchman application.",
    },
    {
        title: 'How often do I complete my tasks?',
        description:
            'Your doctor will create your Watchman profile based on your medical history and will determine how often you have to complete your lab tests, imaging tests, and surveys. Your doctor will let you know through Watchman when your next follow-up will be and you will be notified by email when it is time to complete your tasks.',
    },
    {
        title: "What happens if I can't complete my follow-up tasks within the required time period?",
        description: (
            <>
                If you can't complete your follow-up tasks for any reason, please email {emailWatchman} or contact your doctor's office with the contact
                information provided to you during the welcome visit.
            </>
        ),
    },
    {
        title: "What if I don't have a printer?",
        description:
            'Please check with your community lab centre and/or imaging centre if they will accept a digital copy of your lab and/or imaging requisition. If they do not accept a digital copy, you can access printing services at community centres and libraries in order to print your requisition.',
    },
    {
        title: 'How do I know when the doctor will review my lab results?',
        description:
            "Once you complete your tasks, you will have to wait until your follow-up date (expected response date) for your doctor's review. You will be able to view your doctor's note on the Watchman application.",
    },
    {
        title: 'How do I view my lab results?',
        description: 'Your lab results will be available to view on the Watchman application once they have been released by your lab provider.',
    },
    {
        title: 'How do I know when my next follow-up review is?',
        description: 'You can view your next follow-up review details on the Watchman application through the Watchman Calendar.',
    },
];

const accordionContentSec3 = [
    {
        title: 'Why do I need a virtual appointment?',
        description:
            'You will be asked for a telephone or video appointment if your doctor requires more information and prefers to verbally communicate with you to get a more comprehensive understanding of your testicular cancer.',
    },
    {
        title: 'Why do I need an in-person appointment?',
        description:
            'You will be asked for an in-person appointment if your doctor requires more information and prefers to meet you in person to get a more comprehensive understanding of your testicular cancer.',
    },
    {
        title: 'Will I ever go back to in-person appointments?',
        description:
            'Your doctor will inform you if you need to go back to in-person appointments. If you no longer want to be part of Watchman, you can also go back to in-person or telephone (during COVID-19) appointments.',
    },
];

const accordionContentSec4 = [
    {
        title: 'How can I reset my password and/or security questions?',
        description: <>Please contact {emailWatchman} and we will be happy to set up a call to help answer your questions.</>,
    },
    {
        title: 'Is my personal information secure while using Watchman?',
        description:
            'The information you provide within the Watchman application is stored securely and is password-protected, so it can only be accessed by you and your care team. Please read the [privacy policy] and [terms and conditions] on this site.',
    },
    {
        title: 'What are the terms of service?',
        description:
            'The Terms of Service, located in the Settings tab of this application, includes information about the privacy and security of your health information. Your health information is stored securely and will not be shared with any external services.',
    },
];

function HelpCentrePageContent() {
    const classes = useStyles();

    return (
        <Box pt={4} pb={24} className={classes.helpCentrePageContent}>
            <Box>
                <Typography variant="body1" paragraph>
                    If you need to get in touch with your clinic, please message your care team directly in Watchman.
                </Typography>
                <Typography variant="body1" paragraph>
                    If you are experiencing a health emergency, please call 911 and/or visit a Hospital Emergency Department in your area immediately.
                </Typography>
                <Typography variant="body1" paragraph>
                    For technical assistance, please contact us at {emailWatchman}
                </Typography>
            </Box>
            <Box mt={4}>
                <Typography variant="h2" paragraph>
                    Frequently Asked Questions
                </Typography>
                <Box mt={2}>
                    <Typography variant="h3">Getting Started</Typography>
                    <AccordionWrapper>
                        {accordionContentSec1.map((item, index) => (
                            <Accordion key={index} index={index} title={item.title} description={item.description} />
                        ))}
                    </AccordionWrapper>
                </Box>
                <Box mt={7.5}>
                    <Typography variant="h3">Completing Your Tasks</Typography>
                    <AccordionWrapper>
                        {accordionContentSec2.map((item, index) => (
                            <Accordion key={index} index={index} title={item.title} description={item.description} />
                        ))}
                    </AccordionWrapper>
                </Box>
                <Box mt={7.5}>
                    <Typography variant="h3">Appointments</Typography>
                    <AccordionWrapper>
                        {accordionContentSec3.map((item, index) => (
                            <Accordion key={index} index={index} title={item.title} description={item.description} />
                        ))}
                    </AccordionWrapper>
                </Box>
                <Box mt={7.5}>
                    <Typography variant="h3">Privacy & Security</Typography>
                    <AccordionWrapper>
                        {accordionContentSec4.map((item, index) => (
                            <Accordion key={index} index={index} title={item.title} description={item.description} />
                        ))}
                    </AccordionWrapper>
                </Box>
            </Box>
        </Box>
    );
}

export default function HelpCentrePage() {
    const title = 'Help Centre';
    const history = useHistory();

    const onPrevClick = () => {
        history.goBack();
    };

    return (
        <DetailsLayout title={title} onClick={onPrevClick}>
            <ContentLayout>
                <HelpCentrePageContent />
            </ContentLayout>
        </DetailsLayout>
    );
}
